import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Paper,
  CircularProgress,
  Collapse,
  Button,
  useTheme,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import StopIcon from "@mui/icons-material/Stop";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HomeIcon from "@mui/icons-material/Home";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import BuildIcon from "@mui/icons-material/Build";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import DI from "../../utility/DI";
import { environment } from "../../../Env_data/environment";

// Speech Synthesis & Recognition Helpers for Voice Mode
export const cleanTextForSpeech = (text) => {
  if (!text) return "";
  let clean = text
    // Currency symbols to spoken words
    .replace(/₹\s*([0-9,.]+)/g, "$1 rupees")
    .replace(/₹/g, " rupees ")
    .replace(/\$\s*([0-9,.]+)/g, "$1 dollars")
    // Remove markdown headers
    .replace(/#{1,6}\s*/g, "")
    // Remove bold and italics
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    // Remove inline code
    .replace(/`([^`]+)`/g, "$1")
    // Convert table lines into readable pauses
    .replace(/\|[^\n]+\|/g, (match) => {
      if (/^\|?\s*[-:]+[-| :]*\|?$/.test(match)) return "";
      const cells = match.split("|").filter((s) => s.trim().length > 0);
      return cells.map((s) => s.trim()).join(", ") + ". ";
    })
    // Bullet points to natural list pauses
    .replace(/^[\s*-•]+\s+/gm, "")
    // Links [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove emojis or system markers that sound awkward
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    // Normalize punctuation and pauses
    .replace(/\n+/g, ". ")
    .replace(/\s{2,}/g, " ")
    .trim();

  return clean;
};

export const formatModelName = (model) => {
  if (!model) return "Spent AI";
  const m = model.toLowerCase();
  if (m.includes("qwen")) return "Qwen 3.8 (Free)";
  if (m.includes("nemotron")) return "Nemotron (Free Fallback)";
  if (m.includes("gemma")) return "Gemma 4 (Free)";
  if (m.includes("llama")) return "Llama 3.3 (Free Fallback)";
  return model.replace(":free", "").split("/").pop() || model;
};

export const speakNaturalText = (cleanText, onStart, onEnd, onError) => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    if (onError) onError("Speech synthesis not supported");
    return null;
  }

  window.speechSynthesis.cancel();
  if (!cleanText) return null;

  // Split into natural sentences to avoid the browser 15s audio timeout bug
  const sentences = cleanText.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleanText];
  let currentIndex = 0;
  let isCancelled = false;

  const getBestVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return null;
    return (
      voices.find((v) => v.lang === "en-IN" && (v.name.includes("Google") || v.name.includes("Natural"))) ||
      voices.find((v) => v.lang.startsWith("en") && (v.name.includes("Google") || v.name.includes("Natural") || v.name.includes("Samantha"))) ||
      voices.find((v) => v.lang.startsWith("en")) ||
      voices[0]
    );
  };

  const speakNext = () => {
    if (isCancelled) return;
    if (currentIndex >= sentences.length) {
      if (onEnd) onEnd();
      return;
    }

    const sentence = sentences[currentIndex].trim();
    if (!sentence) {
      currentIndex++;
      speakNext();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(sentence);
    const voice = getBestVoice();
    if (voice) utterance.voice = voice;
    utterance.rate = 1.02;
    utterance.pitch = 1.0;

    utterance.onend = () => {
      if (isCancelled) return;
      currentIndex++;
      speakNext();
    };

    utterance.onerror = (e) => {
      if (isCancelled) return;
      console.warn("Speech synthesis utterance error", e);
      currentIndex++;
      speakNext();
    };

    window.speechSynthesis.speak(utterance);
  };

  if (onStart) onStart();
  speakNext();

  return {
    cancel: () => {
      isCancelled = true;
      window.speechSynthesis.cancel();
      if (onEnd) onEnd();
    },
  };
};

export const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const SUGGESTED_PROMPTS = [
  {
    icon: <HomeIcon fontSize="small" />,
    text: "What homes do I have and their expenses?",
    query: "What homes do I have and their total expenses?",
  },
  {
    icon: <AccountBalanceWalletIcon fontSize="small" />,
    text: "Who owes me money or what are my balances?",
    query: "Who owes me money or what are my net split balances?",
  },
  {
    icon: <PeopleIcon fontSize="small" />,
    text: "List my friends and shared groups",
    query: "Who are my friends and what groups do I have?",
  },
  {
    icon: <TrendingUpIcon fontSize="small" />,
    text: "Summarize my home spending by category",
    query: "Give me a summary of my home expenses by category",
  },
];

// Spent App 12 Standard Categories with Icons & Quick Query Presets
export const SPENT_CATEGORIES = [
  { value: "rashan_ghar", label: "🛒 Rashan / Groceries", query: "rashan_ghar", icon: "🛒", desc: "Groceries & Ration" },
  { value: "kiraya_bijli_pani", label: "🏠 Rent & Bills", query: "kiraya_bijli_pani", icon: "🏠", desc: "Electricity, Water, Rent" },
  { value: "khana_nashta", label: "🍽️ Food & Snacks", query: "khana_nashta", icon: "🍽️", desc: "Eating out, Fast food, Chai" },
  { value: "gadi_petrol_safar", label: "🚗 Petrol & Travel", query: "gadi_petrol_safar", icon: "🚗", desc: "Fuel, Cab, Bus, Travel" },
  { value: "dawai_ilaaj", label: "💊 Medicines", query: "dawai_ilaaj", icon: "💊", desc: "Health & Medicines" },
  { value: "recharge_subscription", label: "📲 Recharge & Net", query: "recharge_subscription", icon: "📲", desc: "Mobile & Wi-Fi" },
  { value: "kapde_lifestyle", label: "👕 Clothes & Lifestyle", query: "kapde_lifestyle", icon: "👕", desc: "Apparel & Gym" },
  { value: "shiksha_fees", label: "📚 Education & Fees", query: "shiksha_fees", icon: "📚", desc: "School & Tuition" },
  { value: "ghar_sudhar", label: "🔧 Home Repairs", query: "ghar_sudhar", icon: "🔧", desc: "Maintenance & Tools" },
  { value: "enjoyment", label: "🎬 Trips & Movies", query: "enjoyment", icon: "🎬", desc: "Entertainment" },
  { value: "tyohar_uphaar", label: "🎁 Gifts & Festival", query: "tyohar_uphaar", icon: "🎁", desc: "Gifts & Shagun" },
  { value: "other_kharche", label: "☕ Other Kharche", query: "other_kharche", icon: "☕", desc: "Miscellaneous" },
];

export const CATEGORY_LOOKUP = SPENT_CATEGORIES.reduce((acc, item) => {
  acc[item.value] = item;
  return acc;
}, {});

export const QUICK_EXPENSE_PRESETS = [
  { label: "🛒 Groceries ₹500", query: "Add 500 groceries", icon: "🛒" },
  { label: "🥛 Milk ₹60", query: "Add 60 milk", icon: "🥛" },
  { label: "🥦 Veggies ₹150", query: "Add 150 vegetables", icon: "🥦" },
  { label: "💡 Electricity ₹1000", query: "Add 1000 electricity", icon: "💡" },
  { label: "⛽ Petrol ₹300", query: "Add 300 petrol", icon: "⛽" },
  { label: "🍽️ Dinner ₹400", query: "Add 400 dinner", icon: "🍽️" },
];

export const QUICK_AMOUNTS = [50, 100, 200, 500, 1000, 2000];

export const getEffectiveActions = (msg, availableHomes = []) => {
  if (msg.actions && msg.actions.length > 0) return msg.actions;
  if (msg.role !== "assistant" || msg.streaming) return [];
  const text = (msg.content || "").toLowerCase();

  // 1. Home Selection fallback: When AI asks which home or lists homes to select from
  const isAskingHome =
    text.includes("which home") ||
    text.includes("specify which home") ||
    text.includes("kis home") ||
    text.includes("select a home") ||
    text.includes("choose a home") ||
    text.includes("destination home") ||
    (text.includes("shows") && text.includes("homes:"));

  if (isAskingHome) {
    const amtMatch = msg.content?.match(/(?:₹|rs\.?|inr)?\s*(\d+(?:\.\d+)?)/i);
    const amt = amtMatch ? amtMatch[1] : "";

    let homeList = (availableHomes && availableHomes.length > 0)
      ? availableHomes.map((h) => (typeof h === "string" ? h : h.name))
      : [];

    if (homeList.length === 0) {
      const nameMatches = [...(msg.content || "").matchAll(/named\s*["']([^"']+)["']/gi)];
      if (nameMatches.length > 0) {
        homeList = nameMatches.map((m) => m[1]);
      }
    }

    if (homeList.length > 0) {
      const homeActions = homeList.map((name) => ({
        label: `🏠 ${name}`,
        query: amt ? `Add ${amt} in ${name}` : `In ${name}`,
        icon: "home",
      }));

      // If text also asks what the expense is for or mentions categories
      if (
        text.includes("what this") ||
        text.includes("expense is for") ||
        text.includes("category") ||
        text.includes("groceries")
      ) {
        homeActions.push(
          { label: "🛒 Groceries", query: amt ? `Groceries ₹${amt}` : "Groceries", icon: "home" },
          { label: "🥛 Milk", query: amt ? `Milk ₹${amt}` : "Milk", icon: "home" },
          { label: "🥦 Veggies", query: amt ? `Vegetables ₹${amt}` : "Vegetables", icon: "home" },
          { label: "💡 Electricity", query: amt ? `Electricity ₹${amt}` : "Electricity", icon: "home" },
          { label: "❌ Cancel", query: "Cancel", icon: "wallet" }
        );
      }
      return homeActions;
    }
  }

  // 2. Category / Expense fallback
  if (
    text.includes("which category") ||
    text.includes("what is the expense for") ||
    text.includes("rashan_ghar") ||
    text.includes("kiraya_bijli_pani") ||
    text.includes("available categories") ||
    text.includes("add this to") ||
    text.includes("add expense to")
  ) {
    return [
      { label: "🛒 Groceries ₹500", query: "Add 500 groceries", icon: "home" },
      { label: "🥛 Milk ₹60", query: "Add 60 milk", icon: "home" },
      { label: "🥦 Veggies ₹150", query: "Add 150 vegetables", icon: "home" },
      { label: "💡 Electricity ₹1000", query: "Add 1000 electricity", icon: "home" },
      { label: "⛽ Petrol ₹300", query: "Add 300 petrol", icon: "home" },
      { label: "🍽️ Dinner ₹400", query: "Add 400 dinner", icon: "home" },
      { label: "🛒 Rashan (Groceries)", query: "rashan_ghar", icon: "home" },
      { label: "🏠 Rent & Bills", query: "kiraya_bijli_pani", icon: "home" },
      { label: "🍽️ Food & Snacks", query: "khana_nashta", icon: "home" },
      { label: "🚗 Petrol & Travel", query: "gadi_petrol_safar", icon: "home" },
      { label: "💊 Medicines", query: "dawai_ilaaj", icon: "home" },
      { label: "📲 Recharge", query: "recharge_subscription", icon: "home" },
      { label: "❌ Cancel", query: "Cancel", icon: "home" },
    ];
  }
  return [];
};

const BlinkingCursor = () => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      display: "inline-block",
      width: "2.5px",
      height: "0.95em",
      bgcolor: "primary.main",
      ml: "3px",
      verticalAlign: "-1px",
      borderRadius: "1px",
      animation: "streamCursorBlink 0.8s infinite ease-in-out",
      "@keyframes streamCursorBlink": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.15 },
      },
    }}
  />
);

// Animated 3-dot typing indicator shown while the AI bubble is empty but streaming
const TypingIndicator = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "5px",
      py: 0.5,
      px: 0.5,
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "primary.main",
          opacity: 0.7,
          animation: "typingDot 1.2s infinite ease-in-out",
          animationDelay: `${i * 0.18}s`,
          "@keyframes typingDot": {
            "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
            "40%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

// Rich Markdown and Table Renderer with Interactive Options
const MarkdownContent = ({ content, isDark, isStreaming, onOptionClick, isDeveloper, activeTool }) => {
  if (!content || !content.trim()) {
    return isStreaming ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.3 }}>
        <TypingIndicator />
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
          {isDeveloper && activeTool ? `⚡ ${activeTool}…` : "thinking…"}
        </Typography>
      </Box>
    ) : null;
  }

  const lines = content.split("\n");
  const elements = [];
  let tableBuffer = [];
  let inTable = false;
  let tableStartIndex = 0;

  const renderFormattedText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*|`.*?`|\*.*?\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
        return (
          <strong key={idx} style={{ color: isDark ? "#f8fafc" : "#0f172a" }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
        const rawCode = part.slice(1, -1).trim();
        const catInfo = CATEGORY_LOOKUP[rawCode.toLowerCase()];
        if (catInfo && onOptionClick) {
          return (
            <Box
              component="button"
              type="button"
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                onOptionClick(catInfo.query || rawCode);
              }}
              title={`Click to select ${catInfo.label}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.22)" : "rgba(99, 102, 241, 0.12)",
                color: isDark ? "#c7d2fe" : "#4338ca",
                border: "1px solid",
                borderColor: isDark ? "rgba(129, 140, 248, 0.45)" : "rgba(99, 102, 241, 0.35)",
                px: 1,
                py: 0.25,
                borderRadius: "8px",
                fontSize: "0.82rem",
                fontFamily: "monospace",
                fontWeight: 700,
                cursor: "pointer",
                verticalAlign: "middle",
                mx: 0.3,
                transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "#ffffff",
                  borderColor: "primary.main",
                  transform: "translateY(-1.5px)",
                  boxShadow: "0 3px 10px rgba(99, 102, 241, 0.35)",
                },
                "&:active": {
                  transform: "scale(0.96)",
                },
              }}
            >
              <span>{rawCode}</span>
              <span style={{ fontSize: "0.72rem" }}>⚡</span>
            </Box>
          );
        }
        return (
          <code
            key={idx}
            style={{
              backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
              padding: "2px 5px",
              borderRadius: "4px",
              fontSize: "0.82rem",
              fontFamily: "monospace",
            }}
          >
            {rawCode}
          </code>
        );
      }
      if (part.startsWith("*") && part.endsWith("*") && part.length >= 2) {
        return <em key={idx}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  const flushTable = () => {
    if (tableBuffer.length === 0) return;
    const parsedRows = tableBuffer
      .filter((row) => !row.match(/^\|?\s*[-:]+[-| :]*\|?$/))
      .map((row) =>
        row
          .split("|")
          .slice(1, -1)
          .map((cell) => cell.trim())
      )
      .filter((row) => row.length > 0);

    if (parsedRows.length > 0) {
      const header = parsedRows[0];
      const body = parsedRows.slice(1);
      elements.push(
        <Box
          key={`tbl-${tableStartIndex}`}
          sx={{
            my: 1.5,
            overflowX: "auto",
            borderRadius: 2,
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}>
                {header.map((h, i) => (
                  <th
                    key={`th-${i}`}
                    style={{
                      padding: "7px 10px",
                      textAlign: "left",
                      fontWeight: 700,
                      borderBottom: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)",
                      color: isDark ? "#e2e8f0" : "#1e293b",
                    }}
                  >
                    {renderFormattedText(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rIdx) => (
                <tr
                  key={`tr-${rIdx}`}
                  style={{
                    backgroundColor:
                      rIdx % 2 === 1
                        ? isDark
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(0,0,0,0.015)"
                        : "transparent",
                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  {row.map((cell, cIdx) => (
                    <td key={`td-${rIdx}-${cIdx}`} style={{ padding: "6px 10px", color: isDark ? "#cbd5e1" : "#334155" }}>
                      {renderFormattedText(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    }
    tableBuffer = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const isTableLine = line.startsWith("|") && (line.endsWith("|") || (isStreaming && i === lines.length - 1));
    const isLast = i === lines.length - 1;

    if (isTableLine) {
      if (!inTable) {
        inTable = true;
        tableStartIndex = i;
      }
      const normalizedLine = line.endsWith("|") ? line : line + " |";
      tableBuffer.push(normalizedLine);
    } else {
      if (inTable) flushTable();
      if (!line) {
        elements.push(<Box key={`sp-${i}`} sx={{ height: 6 }} />);
      } else if (line.startsWith("- ") || line.startsWith("* ")) {
        elements.push(
          <Box
            key={`li-${i}`}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1, my: 0.3, pl: 0.5 }}
          >
            <Typography sx={{ color: "primary.main", fontSize: "0.85rem", lineHeight: 1.5 }}>•</Typography>
            <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: isDark ? "#cbd5e1" : "#334155", wordBreak: "break-word" }}>
              {renderFormattedText(line.slice(2))}
              {isStreaming && isLast && <BlinkingCursor />}
            </Typography>
          </Box>
        );
      } else if (line.startsWith("### ")) {
        elements.push(
          <Typography
            key={`h3-${i}`}
            variant="subtitle2"
            fontWeight={800}
            sx={{ mt: 1, mb: 0.5, color: isDark ? "#f8fafc" : "#0f172a", wordBreak: "break-word" }}
          >
            {renderFormattedText(line.slice(4))}
            {isStreaming && isLast && <BlinkingCursor />}
          </Typography>
        );
      } else {
        elements.push(
          <Typography
            key={`p-${i}`}
            sx={{ fontSize: "0.87rem", lineHeight: 1.55, my: 0.3, color: isDark ? "#cbd5e1" : "#334155", wordBreak: "break-word" }}
          >
            {renderFormattedText(line)}
            {isStreaming && isLast && <BlinkingCursor />}
          </Typography>
        );
      }
    }
  }
  if (inTable) flushTable();

  return (
    <Box sx={{ position: "relative" }}>
      {elements}
    </Box>
  );
};

// Collapsible MCP Tool Execution Log
const ToolExecutionBadge = ({ executedTools, isDark, isDeveloper }) => {
  const [open, setOpen] = useState(false);
  if (!executedTools || executedTools.length === 0) return null;

  const toolNames = executedTools.map((t) => t.tool).join(", ");

  // Normal user mode: clean badge showing which tools were called, without payload and response JSON
  if (!isDeveloper) {
    return (
      <Box sx={{ mb: 1.2 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.8,
            px: 1.2,
            py: 0.4,
            borderRadius: 2,
            bgcolor: isDark ? "rgba(99, 102, 241, 0.12)" : "rgba(99, 102, 241, 0.07)",
            border: "1px solid",
            borderColor: isDark ? "rgba(129, 140, 248, 0.25)" : "rgba(99, 102, 241, 0.18)",
            userSelect: "none",
          }}
        >
          <BuildIcon sx={{ fontSize: 13, color: "primary.main" }} />
          <Typography variant="caption" fontWeight={700} sx={{ color: "primary.main", fontSize: "0.74rem" }}>
            Spent MCP: {toolNames}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Developer mode: expandable accordion with payload and response JSON
  return (
    <Box sx={{ mb: 1.2 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.8,
          px: 1.2,
          py: 0.4,
          borderRadius: 2,
          bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(99, 102, 241, 0.08)",
          border: "1px solid",
          borderColor: isDark ? "rgba(129, 140, 248, 0.3)" : "rgba(99, 102, 241, 0.2)",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <BuildIcon sx={{ fontSize: 13, color: "primary.main" }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: "primary.main", fontSize: "0.74rem" }}>
          Spent MCP (Dev): {toolNames}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 15,
            color: "primary.main",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.8,
            p: 1.2,
            borderRadius: 2,
            bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "rgba(241, 245, 249, 0.8)",
            border: "1px dashed",
            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            fontSize: "0.75rem",
          }}
        >
          {executedTools.map((t, idx) => (
            <Box key={idx} sx={{ mb: idx < executedTools.length - 1 ? 1 : 0 }}>
              <Typography variant="caption" fontWeight={700} display="block" color="text.primary">
                ⚡ Tool: {t.tool}
              </Typography>
              {t.arguments && Object.keys(t.arguments).length > 0 && (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.3,
                    mb: 0.5,
                    p: 0.8,
                    borderRadius: 1,
                    bgcolor: isDark ? "#0b0f19" : "#e2e8f0",
                    fontSize: "0.70rem",
                    overflowX: "auto",
                    color: isDark ? "#38bdf8" : "#0284c7",
                  }}
                >
                  Payload: {JSON.stringify(t.arguments, null, 2)}
                </Box>
              )}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.3,
                  p: 0.8,
                  borderRadius: 1,
                  bgcolor: isDark ? "#020617" : "#f8fafc",
                  fontSize: "0.72rem",
                  overflowX: "auto",
                  color: isDark ? "#94a3b8" : "#475569",
                }}
              >
                {JSON.stringify(t.result, null, 2)}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Helper to detect if an assistant message represents a successfully recorded/added expense
const isRecordAdded = (msg) => {
  if (!msg || msg.role !== "assistant" || msg.streaming) return false;
  const content = (msg.content || "").toLowerCase();

  // If message contains preview or asks for confirmation, it's not yet recorded
  if (
    content.includes("preview") ||
    content.includes("would you like me to confirm") ||
    content.includes("click **✅ confirm") ||
    content.includes("which home would you like")
  ) {
    return false;
  }

  const hasSuccessText =
    content.includes("recorded successfully") ||
    content.includes("added in batch successfully") ||
    content.includes("added successfully") ||
    content.includes("expense recorded successfully") ||
    content.includes("split expense recorded successfully");

  const hasSuccessTool =
    Array.isArray(msg.executedTools) &&
    msg.executedTools.some(
      (t) =>
        (t.tool === "add_home_expense" || t.tool === "add_shared_expense") &&
        t.result &&
        t.result.success !== false &&
        !t.result.error
    );

  return hasSuccessText || hasSuccessTool;
};

// Green animated Done / Added success badge for bottom of message
const RecordSuccessBadge = ({ isDark }) => (
  <Box
    sx={{
      mt: 2,
      mb: 0.5,
      p: 1.5,
      borderRadius: 3,
      background: isDark
        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(5, 150, 105, 0.08) 100%)"
        : "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
      border: "1.5px solid",
      borderColor: isDark ? "rgba(52, 211, 153, 0.4)" : "#10b981",
      display: "flex",
      alignItems: "center",
      gap: 2,
      boxShadow: isDark
        ? "0 4px 16px rgba(16, 185, 129, 0.2)"
        : "0 4px 16px rgba(16, 185, 129, 0.15)",
      animation: "successCardIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
      "@keyframes successCardIn": {
        "0%": { opacity: 0, transform: "scale(0.95) translateY(6px)" },
        "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
      },
    }}
  >
    {/* Big Animated Glowing Green Checkmark Icon */}
    <Box
      sx={{
        width: 54,
        height: 54,
        minWidth: 54,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#ffffff",
        position: "relative",
        boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.6)",
        animation: "pulseGreen 2s infinite",
        "@keyframes pulseGreen": {
          "0%": {
            boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)",
            transform: "scale(0.96)",
          },
          "70%": {
            boxShadow: "0 0 0 16px rgba(16, 185, 129, 0)",
            transform: "scale(1.02)",
          },
          "100%": {
            boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)",
            transform: "scale(0.96)",
          },
        },
      }}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline
          points="20 6 9 17 4 12"
          style={{
            strokeDasharray: 30,
            strokeDashoffset: 0,
            animation: "checkDraw 0.6s ease-in-out",
          }}
        />
      </svg>
    </Box>

    <Box sx={{ flex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: isDark ? "#34d399" : "#065f46",
          fontSize: "0.95rem",
          display: "flex",
          alignItems: "center",
          gap: 0.6,
        }}
      >
        Record Added Successfully!
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: isDark ? "rgba(255,255,255,0.75)" : "#047857",
          display: "block",
          fontSize: "0.78rem",
          fontWeight: 600,
          mt: 0.3,
        }}
      >
        Transaction successfully recorded and balance updated.
      </Typography>
    </Box>
  </Box>
);

const AskAI = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDeveloper = new URLSearchParams(location.search).get("developer") === "1";
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Voice Mode & Speech Synthesis / Recognition states
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef = useRef(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMsgIndex, setSpeakingMsgIndex] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [autoSendCountdown, setAutoSendCountdown] = useState(null);
  const ttsControllerRef = useRef(null);
  const recognitionRef = useRef(null);
  const autoSendTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const fullTranscriptRef = useRef("");

  // Smooth typewriter streaming refs
  const targetTextRef = useRef("");
  const displayedTextRef = useRef("");
  const animFrameIdRef = useRef(null);
  const isStreamActiveRef = useRef(false);
  const streamToolsRef = useRef([]);
  const streamActionsRef = useRef([]);
  const streamModelRef = useRef("qwen/qwen3.8-27b:free");
  const assistantIndexRef = useRef(null);

  const {
    di: { session, GET, urls },
  } = props;

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userHomes, setUserHomes] = useState(session?.homes || []);

  // Fetch homes from backend if not already available
  useEffect(() => {
    if (session?.id && GET && urls?.get?.get_homes) {
      GET(urls.get.get_homes, { user_id: session.id })
        .then((res) => {
          if (res?.success && Array.isArray(res.homes) && res.homes.length > 0) {
            setUserHomes(res.homes);
          }
        })
        .catch((err) => {
          console.error("Error fetching homes in AskAI:", err);
        });
    }
  }, [session?.id, GET, urls]);

  // Compute effective homes list (using fetched homes or extracting from message text)
  const effectiveHomes = useMemo(() => {
    if (userHomes && userHomes.length > 0) return userHomes;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.content) {
      const matches = [...lastMsg.content.matchAll(/named\s*["']([^"']+)["']/gi)];
      if (matches.length > 0) {
        return matches.map((m, idx) => ({ id: `extracted-${idx}`, name: m[1] }));
      }
    }
    return [];
  }, [userHomes, messages]);

  const clearAutoSendTimer = () => {
    if (autoSendTimerRef.current) {
      clearTimeout(autoSendTimerRef.current);
      autoSendTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAutoSendCountdown(null);
  };

  const scheduleAutoSend = (textToSend) => {
    clearAutoSendTimer();
    if (!voiceModeRef.current || !textToSend || !textToSend.trim()) return;

    let secondsLeft = 5;
    setAutoSendCountdown(secondsLeft);

    countdownIntervalRef.current = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft > 0) {
        setAutoSendCountdown(secondsLeft);
      } else {
        setAutoSendCountdown(null);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      }
    }, 1000);

    autoSendTimerRef.current = setTimeout(() => {
      clearAutoSendTimer();
      stopListening();
      if (textToSend && textToSend.trim()) {
        handleSend(textToSend.trim());
      }
    }, 5000);
  };

  const toggleVoiceMode = () => {
    const next = !voiceMode;
    setVoiceMode(next);
    voiceModeRef.current = next;
    if (!next) {
      clearAutoSendTimer();
      stopSpeaking();
      stopListening();
    } else {
      // If turning voice mode ON, immediately activate receiver mic to take voice
      if (!isSpeaking && !loading) {
        setTimeout(() => {
          startListening();
        }, 200);
      }
    }
  };

  const stopSpeaking = () => {
    if (ttsControllerRef.current) {
      ttsControllerRef.current.cancel();
      ttsControllerRef.current = null;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setSpeakingMsgIndex(null);
  };

  const playSpeechForMessage = (text, index) => {
    stopSpeaking();
    clearAutoSendTimer();
    const clean = cleanTextForSpeech(text);
    if (!clean) return;

    setSpeakingMsgIndex(index);
    setIsSpeaking(true);

    const controller = speakNaturalText(
      clean,
      () => {
        setIsSpeaking(true);
        setSpeakingMsgIndex(index);
      },
      () => {
        setIsSpeaking(false);
        setSpeakingMsgIndex(null);
        // Automatically enable receiver mic after spoken voice finishes
        if (voiceModeRef.current) {
          setTimeout(() => {
            if (voiceModeRef.current && !loading) {
              startListening();
            }
          }, 450);
        }
      },
      () => {
        setIsSpeaking(false);
        setSpeakingMsgIndex(null);
      }
    );
    ttsControllerRef.current = controller;
  };

  const stopListening = () => {
    clearAutoSendTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) { }
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const startListening = () => {
    stopSpeaking();
    clearAutoSendTimer();
    fullTranscriptRef.current = "";
    const SpeechRec = getSpeechRecognition();
    if (!SpeechRec) {
      alert("Speech recognition is not supported in this browser. You can type your question or use Chrome/Safari.");
      return;
    }

    try {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) { }
      }

      const rec = new SpeechRec();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-IN";

      rec.onstart = () => {
        setIsListening(true);
      };

      rec.onresult = (e) => {
        let accumulated = "";
        for (let i = 0; i < e.results.length; ++i) {
          accumulated += e.results[i][0].transcript + " ";
        }
        accumulated = accumulated.trim();
        if (accumulated) {
          fullTranscriptRef.current = accumulated;
          setInput(accumulated);
          // Wait at least 5 seconds after speech before auto-submitting.
          // Resets every time the user speaks more words!
          if (voiceModeRef.current) {
            scheduleAutoSend(accumulated);
          }
        }
      };

      rec.onerror = (e) => {
        console.warn("Speech recognition error:", e);
        // Don't kill listening on non-fatal silence timeouts
        if (e.error === "no-speech") {
          return;
        }
        clearAutoSendTimer();
        setIsListening(false);
      };

      rec.onend = () => {
        // If an auto-send timer is currently running, keep listening state active until it fires
        if (!autoSendTimerRef.current) {
          setIsListening(false);
        }
      };

      recognitionRef.current = rec;
      rec.start();
    } catch (err) {
      console.warn("Could not start speech recognition:", err);
      clearAutoSendTimer();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      const textToSubmit = fullTranscriptRef.current || input.trim();
      stopListening();
      if (textToSubmit && voiceModeRef.current) {
        handleSend(textToSubmit);
      }
      return;
    }

    startListening();
  };

  // Reliable auto-scroll: scrolls both the container and into-view anchor to guarantee viewport stays at bottom
  const scrollToBottom = (behavior = "auto", force = true) => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const targetTop = container.scrollHeight + 1000;
      if (behavior === "smooth") {
        container.scrollTo({ top: targetTop, behavior: "smooth" });
      } else {
        container.scrollTop = targetTop;
      }
    }
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({
          behavior: behavior === "smooth" ? "smooth" : "auto",
          block: "end",
        });
      } catch (e) {
        messagesEndRef.current.scrollIntoView(false);
      }
    }
  };

  // Auto-scroll on every messages state change (submit text, streaming tokens, tools, completion)
  useEffect(() => {
    scrollToBottom("auto", true);
    const rAF = requestAnimationFrame(() => scrollToBottom("auto", true));
    const timer = setTimeout(() => scrollToBottom("smooth", true), 80);
    return () => {
      cancelAnimationFrame(rAF);
      clearTimeout(timer);
    };
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      clearAutoSendTimer();
      if (animFrameIdRef.current) {
        cancelAnimationFrame(animFrameIdRef.current);
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) { }
      }
    };
  }, []);

  const startSmoothStream = (assistantIndex) => {
    assistantIndexRef.current = assistantIndex;
    isStreamActiveRef.current = true;

    const tick = () => {
      const target = targetTextRef.current;
      const current = displayedTextRef.current;

      if (current.length < target.length) {
        const diff = target.length - current.length;
        // Dynamic adaptive typing speed for a silky-smooth human typewriter effect:
        let step = 1;
        if (diff > 120) step = Math.ceil(diff / 4);
        else if (diff > 60) step = 5;
        else if (diff > 25) step = 3;
        else if (diff > 8) step = 2;
        else step = 1;

        const nextText = target.slice(0, current.length + step);
        displayedTextRef.current = nextText;

        setMessages((prev) => {
          const copy = [...prev];
          if (copy[assistantIndex]) {
            copy[assistantIndex] = {
              ...copy[assistantIndex],
              content: nextText,
              streaming: true,
              executedTools: streamToolsRef.current,
            };
          }
          return copy;
        });

        scrollToBottom("auto", true);
      }

      // Keep running if network stream is sending or if buffered characters remain
      if (isStreamActiveRef.current || displayedTextRef.current.length < targetTextRef.current.length) {
        animFrameIdRef.current = requestAnimationFrame(tick);
      } else {
        // Stream completed and all buffered text has smoothly typed out
        // Final strip of any actions marker that slipped through
        const cleanText = (targetTextRef.current || "").replace(/\s*<!--actions:[\s\S]*?-->\s*/g, "").trim();
        setMessages((prev) => {
          const copy = [...prev];
          if (copy[assistantIndex]) {
            copy[assistantIndex] = {
              ...copy[assistantIndex],
              content: cleanText || "I could not retrieve a response. Please try again.",
              streaming: false,
              executedTools: streamToolsRef.current,
              model: streamModelRef.current,
              actions: streamActionsRef.current,
            };
          }
          return copy;
        });
        scrollToBottom("smooth");

        // Voice Mode: Automatically speak assistant answer aloud!
        if (voiceModeRef.current && cleanText) {
          playSpeechForMessage(cleanText, assistantIndex);
        }
      }
    };

    if (animFrameIdRef.current) {
      cancelAnimationFrame(animFrameIdRef.current);
    }
    animFrameIdRef.current = requestAnimationFrame(tick);
  };

  const handleSend = async (queryText) => {
    clearAutoSendTimer();
    stopSpeaking();
    stopListening();
    const textToSend = (queryText || input).trim();
    if (!textToSend || loading) return;

    setLoading(true);
    const userMsg = {
      role: "user",
      content: textToSend,
    };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setActiveTool(null);

    // Placeholder for assistant stream
    const assistantIndex = updatedMessages.length;
    targetTextRef.current = "";
    displayedTextRef.current = "";
    streamToolsRef.current = [];
    streamActionsRef.current = [];
    streamModelRef.current = "openrouter/free";
    isStreamActiveRef.current = true;

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: "",
        streaming: true,
        executedTools: [],
      },
    ]);

    startSmoothStream(assistantIndex);

    try {
      const history = updatedMessages.map((m) => ({
        role: m.role || "user",
        content: m.content || "",
      }));

      const apiUrl = `${environment.BASE_URL}ai/chat.php`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: session?.id,
          messages: history,
          stream: true,
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        isStreamActiveRef.current = false;
        if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
        const jsonRes = await response.json();
        const text = jsonRes?.message?.content || jsonRes?.message || jsonRes?.error || "Could not retrieve response.";
        const tools = jsonRes?.executed_tools || [];
        setMessages((prev) => {
          const copy = [...prev];
          if (copy[assistantIndex]) {
            copy[assistantIndex] = {
              ...copy[assistantIndex],
              content: text,
              streaming: false,
              executedTools: tools,
              model: jsonRes?.model || "openrouter/free",
              actions: jsonRes?.actions || [],
            };
          }
          return copy;
        });
        scrollToBottom("smooth");
        if (voiceModeRef.current && text) {
          playSpeechForMessage(text, assistantIndex);
        }
        return;
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // incomplete remainder

        for (const eventBlock of events) {
          const lines = eventBlock.split("\n");
          let eventType = "message";
          let dataStr = "";

          for (const l of lines) {
            if (l.startsWith("event: ")) {
              eventType = l.slice(7).trim();
            } else if (l.startsWith("data: ")) {
              dataStr = l.slice(6).trim();
            }
          }

          if (!dataStr) continue;

          try {
            const parsed = JSON.parse(dataStr);

            if (eventType === "tool_start") {
              setActiveTool(parsed.tool || "tool");
            } else if (eventType === "tool_done") {
              setActiveTool(null);
              streamToolsRef.current = [...streamToolsRef.current, parsed];
              setMessages((prev) => {
                const copy = [...prev];
                if (copy[assistantIndex]) {
                  copy[assistantIndex] = {
                    ...copy[assistantIndex],
                    executedTools: streamToolsRef.current,
                  };
                }
                return copy;
              });
            } else if (eventType === "token") {
              setActiveTool(null);
              targetTextRef.current += parsed.token || "";
            } else if (eventType === "done") {
              setActiveTool(null);
              streamModelRef.current = parsed.model || streamModelRef.current;
              // Use actions from 'done' payload first
              let finalActions = parsed.actions || [];
              // Also try to extract embedded <!--actions:[...]--> from the accumulated token stream
              const actionsMatch = targetTextRef.current.match(/<!--actions:(.*?)-->/s);
              if (actionsMatch) {
                try {
                  const embedded = JSON.parse(actionsMatch[1]);
                  if (Array.isArray(embedded) && embedded.length > 0) {
                    finalActions = embedded;
                  }
                } catch (e) { /* ignore parse error */ }
                // Strip the actions marker from displayed text BEFORE typewriter finishes
                targetTextRef.current = targetTextRef.current.replace(/\s*<!--actions:[\s\S]*?-->\s*/g, "").trim();
                displayedTextRef.current = displayedTextRef.current.replace(/\s*<!--actions:[\s\S]*?-->\s*/g, "").trim();
              }
              streamActionsRef.current = finalActions;
              if (parsed.full_text && !targetTextRef.current) {
                targetTextRef.current = parsed.full_text.replace(/\s*<!--actions:[\s\S]*?-->\s*/g, "").trim();
              }
            } else if (eventType === "error") {
              setActiveTool(null);
              targetTextRef.current = parsed.message || "An error occurred connecting to AI.";
              displayedTextRef.current = targetTextRef.current;
              isStreamActiveRef.current = false;
            }
          } catch (e) {
            console.warn("SSE json parse error", e);
          }
        }
      }

      // Check for leftover events in buffer
      if (buffer && buffer.trim()) {
        const lines = buffer.split("\n");
        let eventType = "message";
        let dataStr = "";
        for (const l of lines) {
          if (l.startsWith("event: ")) eventType = l.slice(7).trim();
          else if (l.startsWith("data: ")) dataStr = l.slice(6).trim();
        }
        if (dataStr) {
          try {
            const parsed = JSON.parse(dataStr);
            if (eventType === "token") targetTextRef.current += parsed.token || "";
            else if (eventType === "done") {
              if (parsed.full_text && !targetTextRef.current) targetTextRef.current = parsed.full_text;
              if (parsed.model) streamModelRef.current = parsed.model;
              if (parsed.actions) streamActionsRef.current = parsed.actions;
            } else if (eventType === "error") {
              targetTextRef.current = parsed.message || targetTextRef.current;
            }
          } catch (e) { }
        }
      }

      // End of network stream: let rAF typewriter finish smoothly draining any remaining buffer
      isStreamActiveRef.current = false;
    } catch (e) {
      console.error("Chat error", e);
      isStreamActiveRef.current = false;
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
      setMessages((prev) => {
        const copy = [...prev];
        if (copy[assistantIndex]) {
          copy[assistantIndex] = {
            ...copy[assistantIndex],
            content: "An issue occurred connecting to Spent AI. Please try again in a moment.",
            streaming: false,
          };
        }
        return copy;
      });
    } finally {
      setLoading(false);
      setActiveTool(null);
    }
  };

  const userName = session?.firstname || "there";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
        maxHeight: "900px",
        maxWidth: "720px",
        mx: "auto",
        position: "relative",
        pb: "90px",
      }}
    >
      {/* Top Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/panel");
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700}>
            Ask AI
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              px: 0.8,
              py: 0.2,
              borderRadius: 2,
              bgcolor: isDark ? "rgba(129, 140, 248, 0.18)" : "rgba(99, 102, 241, 0.1)",
              color: isDark ? "#c7d2fe" : "#4f46e5",
              fontSize: "0.72rem",
              fontWeight: 700,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 12 }} />
            {isDeveloper ? "OpenRouter + MCP (Dev)" : "AI Assistant"}
          </Box>
        </Box>

        {/* Voice Mode Toggle Pill */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={voiceMode ? "Voice Mode: ON (AI reads responses aloud)" : "Voice Mode: OFF (Click to enable audio speech)"}>
            <Box
              onClick={toggleVoiceMode}
              role="button"
              tabIndex={0}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.2,
                py: 0.4,
                borderRadius: "20px",
                cursor: "pointer",
                bgcolor: voiceMode
                  ? "primary.main"
                  : isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.05)",
                color: voiceMode
                  ? "#ffffff"
                  : isDark
                    ? "#94a3b8"
                    : "#64748b",
                border: "1px solid",
                borderColor: voiceMode
                  ? "primary.light"
                  : isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                fontSize: "0.74rem",
                fontWeight: 700,
                transition: "all 0.2s ease",
                boxShadow: voiceMode ? "0 2px 10px rgba(99, 102, 241, 0.4)" : "none",
                "&:hover": {
                  transform: "scale(1.02)",
                  bgcolor: voiceMode ? "primary.dark" : isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                },
              }}
            >
              {voiceMode ? (
                <>
                  <VolumeUpIcon sx={{ fontSize: 15 }} />
                  <span>Voice ON</span>
                  {isSpeaking && (
                    <GraphicEqIcon
                      sx={{
                        fontSize: 14,
                        animation: "voiceEq 1s infinite ease-in-out",
                        "@keyframes voiceEq": {
                          "0%, 100%": { transform: "scaleY(0.6)", opacity: 0.6 },
                          "50%": { transform: "scaleY(1.2)", opacity: 1 },
                        },
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  <VolumeOffIcon sx={{ fontSize: 15 }} />
                  <span>Voice OFF</span>
                </>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Conversation & Empty State Area */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              my: "auto",
              textAlign: "center",
              px: 2,
            }}
          >
            {/* Glowing Orb */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: isDark
                  ? "radial-gradient(circle, rgba(129, 140, 248, 0.8) 0%, rgba(99, 102, 241, 0.2) 70%, transparent 100%)"
                  : "radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, rgba(165, 180, 252, 0.2) 70%, transparent 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
                boxShadow: "0 0 32px rgba(99, 102, 241, 0.35)",
                animation: "pulse 3s infinite ease-in-out",
                "@keyframes pulse": {
                  "0%": { transform: "scale(0.96)", opacity: 0.85 },
                  "50%": { transform: "scale(1.05)", opacity: 1 },
                  "100%": { transform: "scale(0.96)", opacity: 0.85 },
                },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 32, color: "#ffffff" }} />
            </Box>

            <Typography variant="h6" fontWeight={800} color="text.primary" gutterBottom>
              Hi {userName}, how can I help?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 420, mb: 3.5, fontSize: "0.88rem" }}
            >
              Powered by Spent AI Free Tier & auto-configured with your Spent App MCP Server. Ask about your homes, friends, balances, or record expenses.
            </Typography>

            {/* Suggested Prompt Chips */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.2,
                justifyContent: "center",
                maxWidth: 520,
              }}
            >
              {SUGGESTED_PROMPTS.map((p, idx) => (
                <Chip
                  key={idx}
                  icon={p.icon}
                  label={p.text}
                  onClick={() => handleSend(p.query)}
                  sx={{
                    py: 2.2,
                    px: 1,
                    borderRadius: 3,
                    bgcolor: isDark ? "rgba(30, 41, 59, 0.7)" : "rgba(255, 255, 255, 0.9)",
                    border: "1px solid",
                    borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
                    cursor: "pointer",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      bgcolor: isDark ? "rgba(51, 65, 85, 0.8)" : "#f8fafc",
                      borderColor: "primary.main",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                gap: 1,
              }}
            >

              {msg.role === "user" ? (
                <Box
                  sx={{
                    maxWidth: "80%",
                    bgcolor: isDark ? "#3b82f6" : "#2563eb",
                    color: "#ffffff",
                    px: 2,
                    py: 1.2,
                    borderRadius: "18px 18px 4px 18px",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                  }}
                >
                  {msg.content}
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, maxWidth: "90%" }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      mt: 0.3,
                      bgcolor: "primary.main",
                      flexShrink: 0,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: isDark ? "rgba(30, 41, 59, 0.85)" : "#ffffff",
                      border: "1px solid",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                      borderRadius: "18px 18px 18px 4px",
                      p: 2,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    }}
                  >
                    {/* MCP Tool Execution Badge: clean badge for user, payload/json for developer */}
                    {msg.executedTools && msg.executedTools.length > 0 && (
                      <ToolExecutionBadge
                        executedTools={msg.executedTools}
                        isDark={isDark}
                        isDeveloper={isDeveloper}
                      />
                    )}

                    {/* Formatted Content with streaming cursor & interactive option badges */}
                    <MarkdownContent
                      content={msg.content}
                      isDark={isDark}
                      isStreaming={msg.streaming}
                      onOptionClick={(q) => handleSend(q)}
                      isDeveloper={isDeveloper}
                      activeTool={activeTool}
                    />

                    {/* Green Done / Added Success Badge if record added successfully */}
                    {isRecordAdded(msg) && (
                      <RecordSuccessBadge isDark={isDark} />
                    )}

                    {/* Contextual Smart Action Buttons (with Category, Home & Option fallbacks) */}
                    {(() => {
                      const effectiveActions = getEffectiveActions(msg, effectiveHomes);
                      if (!effectiveActions || effectiveActions.length === 0) return null;

                      return (
                        <Box
                          sx={{
                            mt: 1.8,
                            pt: 1.2,
                            borderTop: "1px dashed",
                            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {effectiveActions.map((act, aIdx) => {
                            const isConfirm = act.label && act.label.includes("Confirm");
                            const isCancel = act.label && act.label.includes("Cancel");

                            return (
                              <Button
                                key={aIdx}
                                size="small"
                                variant={isConfirm ? "contained" : "outlined"}
                                onClick={() => {
                                  if (act.query) {
                                    handleSend(act.query);
                                  } else if (act.path) {
                                    navigate(act.path);
                                  }
                                }}
                                startIcon={
                                  act.icon === "home" ? (
                                    <HomeIcon fontSize="small" />
                                  ) : act.icon === "wallet" ? (
                                    <AccountBalanceWalletIcon fontSize="small" />
                                  ) : act.icon === "group" ? (
                                    <PeopleIcon fontSize="small" />
                                  ) : null
                                }
                                sx={{
                                  borderRadius: 3,
                                  textTransform: "none",
                                  fontSize: "0.80rem",
                                  fontWeight: 700,
                                  py: 0.5,
                                  px: 1.3,
                                  transition: "all 0.2s ease",
                                  ...(isConfirm
                                    ? {
                                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                      color: "#ffffff",
                                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                                      "&:hover": {
                                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                        boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)",
                                        transform: "translateY(-1px)",
                                      },
                                    }
                                    : isCancel
                                      ? {
                                        borderColor: isDark ? "rgba(239, 68, 68, 0.4)" : "rgba(239, 68, 68, 0.5)",
                                        color: isDark ? "#fca5a5" : "#dc2626",
                                        "&:hover": {
                                          bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.08)",
                                          borderColor: "#ef4444",
                                          transform: "translateY(-1px)",
                                        },
                                      }
                                      : {
                                        borderColor: isDark ? "rgba(129, 140, 248, 0.4)" : "rgba(99, 102, 241, 0.35)",
                                        color: isDark ? "#c7d2fe" : "primary.main",
                                        bgcolor: isDark ? "rgba(99, 102, 241, 0.08)" : "rgba(99, 102, 241, 0.04)",
                                        "&:hover": {
                                          bgcolor: "primary.main",
                                          color: "#ffffff",
                                          borderColor: "primary.main",
                                          transform: "translateY(-1px)",
                                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                                        },
                                      }),
                                }}
                              >
                                {act.label}
                              </Button>
                            );
                          })}
                        </Box>
                      );
                    })()}

                    {/* Message Footer: Voice Listen button and Model attribution */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 1.5,
                        pt: 0.8,
                        borderTop: "1px solid",
                        borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                      }}
                    >
                      {!msg.streaming && msg.content && (
                        <Tooltip title={isSpeaking && speakingMsgIndex === index ? "Stop speaking" : "Listen to this response aloud"}>
                          <Button
                            size="small"
                            onClick={() => {
                              if (isSpeaking && speakingMsgIndex === index) {
                                stopSpeaking();
                              } else {
                                playSpeechForMessage(msg.content, index);
                              }
                            }}
                            startIcon={
                              isSpeaking && speakingMsgIndex === index ? (
                                <StopIcon sx={{ fontSize: 15 }} />
                              ) : (
                                <VolumeUpIcon sx={{ fontSize: 15 }} />
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              py: 0.2,
                              px: 0.9,
                              borderRadius: 2,
                              color: isSpeaking && speakingMsgIndex === index
                                ? "primary.main"
                                : isDark ? "#94a3b8" : "#64748b",
                              bgcolor: isSpeaking && speakingMsgIndex === index
                                ? isDark ? "rgba(99, 102, 241, 0.18)" : "rgba(99, 102, 241, 0.08)"
                                : isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                              border: "1px solid",
                              borderColor: isSpeaking && speakingMsgIndex === index
                                ? "primary.main"
                                : isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            {isSpeaking && speakingMsgIndex === index ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <span>Speaking</span>
                                <GraphicEqIcon
                                  sx={{
                                    fontSize: 14,
                                    animation: "msgVoiceEq 0.9s infinite ease-in-out",
                                    "@keyframes msgVoiceEq": {
                                      "0%, 100%": { opacity: 0.4 },
                                      "50%": { opacity: 1 },
                                    },
                                  }}
                                />
                              </Box>
                            ) : (
                              "Listen"
                            )}
                          </Button>
                        </Tooltip>
                      )}

                      {/* Model attribution badge */}
                      {msg.model && !msg.streaming && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.68rem",
                            color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                            ml: "auto",
                          }}
                        >
                          Via Spent AI: {formatModelName(msg.model)}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          ))
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Floating Dark Omnibar Pill (Matching Screen 6) */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 40, sm: 40 },
          left: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          maxWidth: 700,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          bgcolor: isListening
            ? isDark ? "rgba(30, 27, 75, 0.96)" : "#1e1b4b"
            : isDark ? "#0f172a" : "#111827",
          color: "#ffffff",
          borderRadius: "36px",
          px: 1.8,
          py: { xs: 0.9, sm: 1.1 },
          minHeight: { xs: 54, sm: 58 },
          boxShadow: isListening
            ? "0 0 0 2px #6366f1, 0 16px 40px rgba(99, 102, 241, 0.45)"
            : isDark
              ? "0 14px 40px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 16px 40px rgba(17, 24, 39, 0.28)",
          zIndex: 1300,
          transition: "all 0.25s ease",
        }}
      >
        {isListening && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1,
              py: 0.35,
              borderRadius: 2,
              bgcolor: autoSendCountdown !== null ? "rgba(99, 102, 241, 0.28)" : "rgba(239, 68, 68, 0.22)",
              color: autoSendCountdown !== null ? "#c7d2fe" : "#f87171",
              fontSize: "0.74rem",
              fontWeight: 700,
              mr: 0.8,
              animation: "listeningPulse 1.2s infinite ease-in-out",
              "@keyframes listeningPulse": {
                "0%, 100%": { opacity: 0.75, transform: "scale(0.97)" },
                "50%": { opacity: 1, transform: "scale(1.03)" },
              },
            }}
          >
            <GraphicEqIcon sx={{ fontSize: 14 }} />
            <span>
              {autoSendCountdown !== null
                ? `Auto-send in ${autoSendCountdown}s`
                : "Listening..."}
            </span>
          </Box>
        )}

        <Box
          component="input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            isListening
              ? autoSendCountdown !== null
                ? `Pausing... auto-submitting in ${autoSendCountdown}s (speak more or tap Send)`
                : "Listening... speak now (e.g. 'Add 400 for groceries in home')"
              : "Ask anything about your spending or homes (e.g. 'Add 400 of paneer')..."
          }
          sx={{
            flex: 1,
            border: "none",
            outline: "none",
            bgcolor: "transparent",
            color: "#ffffff",
            fontSize: "0.96rem",
            fontWeight: 500,
            py: 0.8,
            px: 1.2,
            minHeight: 36,
            lineHeight: 1.5,
            "&::placeholder": {
              color: isListening ? "#c7d2fe" : "rgba(255,255,255,0.48)",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          {/* Voice Input Mic Button */}
          <Tooltip title={isListening ? "Stop listening" : "Speak to AI (Voice Input)"}>
            <IconButton
              size="medium"
              onClick={toggleListening}
              sx={{
                bgcolor: isListening
                  ? "#ef4444"
                  : "rgba(255,255,255,0.12)",
                color: "#ffffff",
                p: 1.0,
                transition: "all 0.2s ease",
                animation: isListening ? "micPulse 1s infinite alternate" : "none",
                "@keyframes micPulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.4)" },
                  "100%": { boxShadow: "0 0 0 8px rgba(239, 68, 68, 0)" },
                },
                "&:hover": {
                  bgcolor: isListening ? "#dc2626" : "rgba(255,255,255,0.22)",
                  transform: "scale(1.06)",
                },
              }}
            >
              {isListening ? <MicOffIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          {/* Send Button */}
          {input.trim() && (
            <Tooltip title="Send message">
              <IconButton
                size="medium"
                onClick={() => handleSend()}
                sx={{
                  bgcolor: "primary.main",
                  color: "#ffffff",
                  p: 1.0,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "primary.dark",
                    transform: "scale(1.06)",
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DI(AskAI);
